import VueRouter from "vue-router";
import Urls from "../urls";
import NProgress from "nprogress";
import { useUserStore } from "../store/user";
import { useChallengesStore } from "@/store/challengeStore";

const router = new VueRouter({
  mode: "history",
  base: process.env.routerBase,
  routes: [
    {
      path: "/",
      name: "home",
      component: () => import("../pages/Home.vue"),
    },
    {
      path: "/users",
      name: "userList",
      component: () => import("../pages/admin/UserList.vue"),
      meta: { ncaaAppAdminOnly: true },
    },
    {
      // fake route that just redirects to server
      path: "/imitate_login",
      name: "imitateLogin",
      props: true,
      beforeEnter: (to, from, next) => {
        location.assign(Urls.imitateLoginUrl(to.params.email));
      },
    },
    {
      path: "/schools",
      name: "schools",
      component: () => import("../pages/admin/SchoolList.vue"),
      meta: { ncaaStaffOnly: true },
    },
    {
      path: "/years/:yearId/selected-schools",
      name: "yearSelectedSchoolList",
      component: () => import("../pages/admin/SchoolList.vue"),
      meta: { requiresAuth: false },
      props(route) {
        const props = { ...route.params };
        props.yearId = parseInt(props.yearId);
        return props;
      },
    },
    {
      path: "/current-year/:sportCode/:division/schools",
      name: "currentSelectedSchoolList",
      component: () => import("../pages/admin/SchoolList.vue"),
      meta: { requiresAuth: false },
      props(route) {
        const props = { ...route.params };
        props.division = parseInt(props.division);
        return props;
      },
    },
    {
      path: "/schools/print",
      name: "lineUpFormsForPrint",
      component: () => import("../pages/admin/SetOfLineUpFormsForPrint.vue"),
      meta: { ncaaAppAdminOnly: true },
    },
    {
      path: "/year/:yearId/schools/submissions/:id",
      name: "lineupForm",
      component: () => import("../pages/LineupForm.vue"),
      meta: { requiresAuth: true },
      props(route) {
        const props = { ...route.params };
        props.id = parseInt(props.id);
        props.yearId = parseInt(props.yearId);
        return props;
      },
    },
    {
      path: "/public/year/:yearId/schools/submissions/:id",
      name: "publicLineupForm",
      component: () => import("../pages/LineupForm.vue"),
      props(route) {
        const props = { ...route.params };
        props.id = parseInt(props.id);
        props.yearId = parseInt(props.yearId);
        if (route.query.code) props.readOnly = false;
        else props.readOnly = true;
        return props;
      },
    },
    {
      path: "/challenges",
      name: "challenges",
      meta: { requiresAuth: true },
      beforeEnter: (to, from, next) => {
        // check that user is staff or committee member.
        const user = useUserStore();
        if (!(user.isLoggedIn && (user.isStaff || user.isCommitteeMember)))
          next("/forbidden");
        else next(true);
      },
      component: () => import("../pages/admin/ChallengesList.vue"),
    },
    {
      path: "/year/:yearId/schools/submissions/:submissionId/challenge/:id",
      name: "challengeForm",
      component: () => import("../pages/LineupChallengeForm.vue"),
      meta: { requiresAuth: true },
      beforeEnter: (to, from, next) => {
        // check that user is staff or committee member.
        const user = useUserStore();
        if (!(user.isLoggedIn && (user.isStaff || user.isCommitteeMember)))
          next("/forbidden");
        useChallengesStore()
          .setOpenChallengeForm(to.params.id, to.params.submissionId)
          .then(() => next(true));
      },
      props(route) {
        const props = { ...route.params };
        props.id = parseInt(props.id);
        props.yearId = parseInt(props.yearId);
        props.submissionId = parseInt(props.submissionId);
        return props;
      },
    },
    {
      path: "/year/:yearId/schools/submissions/:submissionId/challenge/new",
      name: "newChallengeForm",
      meta: { requiresAuth: true },
      component: () => import("../pages/LineupChallengeForm.vue"),
      props(route) {
        const props = { ...route.params };
        props.submissionId = parseInt(props.submissionId);
        props.yearId = parseInt(props.yearId);
        return props;
      },
      beforeEnter: (to, from, next) => {
        useChallengesStore()
          .setNewChallengeForm()
          .then(() => next(true));
      },
    },
    {
      path: "/year/:yearId/schools/submissions/:submissionId/challenge/:id/view",
      name: "viewSubmitRebuttalForm",
      component: () => import("../pages/RebuttalForm.vue"),
      props(route) {
        const props = { ...route.params };
        props.submissionId = parseInt(props.submissionId);
        props.challengeFormId = parseInt(props.id);
        props.yearId = parseInt(props.yearId);
        return props;
      },
      beforeEnter: (to, from, next) => {
        const userStore = useUserStore();
        userStore.loading = true;
        useChallengesStore()
          .setNewRebuttalForm(
            to.params.yearId,
            to.params.submissionId,
            to.params.id
          )
          .then(() => {
            next(true);
          })
          .finally(() => {
            userStore.loading = false;
          });
      },
    },
    {
      // fake route that just redirects to server
      path: "/login",
      name: "login",
      beforeEnter: (to, from, next) => {
        location.assign(Urls.login);
      },
    },
    {
      // fake route that just redirects to server
      path: "/logout",
      name: "logout",
      beforeEnter: (to, from, next) => {
        sessionStorage.clear();
        location.assign(Urls.logout);
      },
    },
    {
      path: "/admin",
      name: "admin",
      component: () => import("../pages/admin/AdminHome.vue"),
      meta: { ncaaAppAdminOnly: true },
      children: [
        {
          path: "",
          name: "formText",
          component: () => import("../components/admin/FormText.vue"),
        },
        {
          path: "individual",
          name: "individualCtl",
          component: () => import("../components/admin/MatrixControls.vue"),
          props: { type: "individual" },
        },
        {
          path: "doubles",
          name: "doublesCtl",
          component: () => import("../components/admin/MatrixControls.vue"),
          props: { type: "doubles" },
        },
        {
          path: "dates",
          name: "dates",
          component: () => import("../components/admin/AvailabilityDates.vue"),
        },
        {
          path: "itaMappings",
          name: "itaMappings",
          component: () => import("../components/admin/ItaMapping.vue"),
        },
        {
          path: "ita-matches",
          name: "itaMatches",
          component: () => import("../components/admin/ItaMatches.vue"),
        },
      ],
    },
    {
      path: "*",
      redirect: { name: "pageNotFound" },
    },
    {
      path: "/pageNotFound",
      name: "pageNotFound",
      component: () => import("../pages/errors/NotFound.vue"),
      props: true,
    },
    {
      path: "/forbidden",
      name: "forbidden",
      component: () => import("../pages/errors/Forbidden.vue"),
    },
  ],
});

router.beforeEach((to, from, next) => {
  NProgress.start();
  const user = useUserStore();
  if (
    to.matched.some((record) => record.meta.requiresAuth) &&
    !user.isLoggedIn
  ) {
    next({ name: "home" });
  } else if (
    to.matched.some((record) => record.meta.ncaaAppAdminOnly) &&
    !user.isNcaaAppAdmin
  ) {
    next({ name: "home" });
  } else if (
    to.matched.some((record) => record.meta.ncaaStaffOnly) &&
    !user.isStaff
  ) {
    next({ name: "home" });
  } else {
    next();
  }
});
router.afterEach((to, from) => {
  NProgress.done();
});

export default router;
